import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, X } from "lucide-react";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const ClassFormatModal = ({
  format,
  open,
  onOpenChange,
  onViewLive,
  onCloseAutoFocus,
}) => {
  if (!format) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[100] bg-black/80" />
        <DialogPrimitive.Content
          data-testid={`class-format-modal-${format.id}`}
          className="fixed left-1/2 top-1/2 z-[101] max-h-[90vh] w-[calc(100%-1.5rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 p-0 shadow-lg duration-200 focus:outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] data-[state=open]:zoom-in-95"
          onCloseAutoFocus={onCloseAutoFocus}
          style={{
            backgroundColor: "var(--surface-3)",
            borderRadius: "22px",
            boxShadow: "var(--shadow-modal)",
          }}
        >
          <DialogPrimitive.Close
            type="button"
            data-testid="class-format-modal-close-button"
            aria-label="Close class details"
            className="modal-close-button absolute right-4 top-4 z-[2]"
          >
            <X size={19} strokeWidth={2} />
          </DialogPrimitive.Close>

          <div className="grid max-h-[90vh] overflow-y-auto md:grid-cols-2">
            {/* Image */}
            <div
              className="relative hidden md:block"
              style={{ minHeight: "100%" }}
            >
              <img
                src={format.image}
                alt={format.name}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to right, rgba(11,10,8,0.1), rgba(34,28,21,0.55))",
                }}
              />
            </div>

            {/* Details */}
            <div className="p-7 sm:p-9">
              <div className="pr-16 sm:pr-14">
                <p className="eyebrow">{format.days}</p>
                <DialogTitle asChild>
                  <h3
                    className="display mt-3"
                    style={{ fontSize: "2rem", fontWeight: 500 }}
                  >
                    {format.name}
                  </h3>
                </DialogTitle>
                <p
                  className="font-serif mt-1"
                  style={{
                    fontStyle: "italic",
                    color: "var(--bronze)",
                    fontSize: "1.05rem",
                  }}
                >
                  {format.tagline}
                </p>
              </div>

              <DialogDescription asChild>
                <p
                  className="mt-5"
                  style={{
                    color: "var(--text-2)",
                    fontSize: "0.92rem",
                    lineHeight: 1.7,
                  }}
                >
                  {format.description}
                </p>
              </DialogDescription>

              <div className="mt-6">
                <p className="eyebrow" style={{ color: "var(--muted)" }}>
                  Equipment
                </p>
                <ul className="mt-3 space-y-2">
                  {(format.equipment || []).map((eq) => (
                    <li
                      key={eq}
                      className="flex items-start gap-2"
                      style={{ color: "var(--text-2)", fontSize: "0.88rem" }}
                    >
                      <Check
                        size={15}
                        style={{ color: "var(--bronze)", marginTop: 3 }}
                      />
                      {eq}
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className="mt-6 pt-5"
                style={{ borderTop: "1px solid var(--border-c)" }}
              >
                <p className="eyebrow" style={{ color: "var(--muted)" }}>
                  Focus
                </p>
                <p
                  className="mt-2"
                  style={{
                    color: "var(--text)",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                  }}
                >
                  {format.focus}
                </p>
              </div>

              <button
                type="button"
                data-testid={`class-format-modal-live-${format.id}`}
                onClick={onViewLive}
                className="btn-fx btn-primary mt-7 w-full"
              >
                View the Live Schedule
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
